/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { SYNCGROUPS_TYPE_SETFEATURES } from './constants';
import type { SynchronizationGroupState } from './types';

export const FEATURE_GROUP_PREFIX = 'Features_';

const emptyTargetIds: string[] = [];

export const getFeatureGroupId = (panelId: string): string =>
  `${FEATURE_GROUP_PREFIX}${panelId}`;

export const getLinkedTargetIdsForFeatureSourcePanel = (
  syncState: SynchronizationGroupState | undefined,
  panelId?: string,
): string[] => {
  if (!panelId) {
    return emptyTargetIds;
  }

  const sourceTargets = syncState?.sources?.byId?.[panelId]?.targets;
  if (!sourceTargets) {
    return emptyTargetIds;
  }
  return sourceTargets.allIds.filter(
    (targetId) => sourceTargets.byId[targetId]?.linked,
  );
};

export const getFeatureLinksForAllPanelsFromState = (
  syncState: SynchronizationGroupState | undefined,
): Record<string, string[]> => {
  if (!syncState) {
    return {};
  }

  const linksFromSources = syncState.sources.allIds.reduce<
    Record<string, string[]>
  >((acc, sourceId) => {
    const source = syncState.sources.byId[sourceId];
    if (
      !source?.types.includes(SYNCGROUPS_TYPE_SETFEATURES) ||
      !source.targets
    ) {
      return acc;
    }

    return {
      ...acc,
      [sourceId]: source.targets.allIds.filter(
        (targetId) => source.targets?.byId[targetId]?.linked,
      ),
    };
  }, {});

  return linksFromSources;
};

export const getLinkedFeatureSourcePanelIdsForMapId = (
  linksByPanelId: Record<string, string[]>,
  mapId: string,
): string[] =>
  Object.keys(linksByPanelId).filter((panelId) =>
    linksByPanelId[panelId]?.includes(mapId),
  );
